import React, {useEffect, useRef, useState} from "react";
import {useNavigate} from "react-router-dom";
import SearchIcon from '@mui/icons-material/Search';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import classnames from "classnames";
import {useUserContext} from "../../contexts/UserContext";
import './PricingListPage.scss'

// Full brand catalog. Moved from the removed PricingListBrands sidebar so the
// dropdown can always reach every brand, not just the user's makes_list.
const MAKES_LIST = ['Acura', 'Alfa Romeo', 'Aston Martin', 'Audi', 'Bentley', 'BMW', 'Buick', 'Cadillac', 'Chevrolet', 'Chrysler', 'Dodge', 'FIAT', 'Ford', 'Ferrari', 'Fisker', 'GMC', 'Genesis', 'Honda', 'Hyundai', 'INEOS', 'INFINITI', 'Jaguar', 'Jeep', 'Kia', 'Lexus', 'Lamborghini', 'Lincoln', 'Lotus', 'Land Rover', 'Lucid', 'Mazda', 'Mercedes-Benz', 'Mitsubishi', 'McLaren', 'MINI', 'Maserati', 'Nissan', 'Porsche', 'Polestar', 'Rivian', 'Ram', 'Subaru', 'Toyota', 'Tesla', 'VinFast', 'Volvo', 'Volkswagen']

/**
 * Searchable brand selector shown in the pricing topbar. Replaces the old
 * brands sidebar: the trigger displays the brand from the /pricing/:make
 * route, and picking an option navigates to that brand's route — persistence
 * keeps working through the existing pages_visited mechanism in HomePage.
 */
export const PricingListBrandDropdown = ({ make }) => {
    const navigate = useNavigate()
    const { profileState } = useUserContext()

    const [isOpen, setIsOpen] = useState(false)
    const [search, setSearch] = useState('')

    const containerRef = useRef(null)
    const searchInputRef = useRef(null)

    const filteredMakes = () => {
        if (search.trim().length) {
            return MAKES_LIST.filter((m) => m.toLowerCase().startsWith(search.toLowerCase()))
        }
        return MAKES_LIST
    }

    const closeDropdown = () => {
        setIsOpen(false)
        setSearch('')
    }

    const selectMake = (selectedMake) => {
        closeDropdown()
        navigate(`/pricing/${selectedMake}`)
    }

    const isSelected = (m) => m.toLowerCase() === make?.toLowerCase()

    const hasDeal = (m) => Boolean(profileState?.makes_with_pricings?.[m.toLowerCase()])

    // While open: focus the search box, close on outside click or Escape.
    useEffect(function manageOpenDropdown() {
        if (!isOpen) return;

        const onMouseDown = (e) => {
            if (!containerRef.current?.contains(e.target)) closeDropdown()
        }
        const onKeyDown = (e) => {
            if (e.key === 'Escape') closeDropdown()
        }

        searchInputRef.current?.focus()
        document.addEventListener('mousedown', onMouseDown)
        document.addEventListener('keydown', onKeyDown)
        return () => {
            document.removeEventListener('mousedown', onMouseDown)
            document.removeEventListener('keydown', onKeyDown)
        }
    }, [isOpen])

    const renderOption = (m) => {
        return (
            <li
                key={m}
                role="option"
                aria-selected={isSelected(m)}
                className={classnames('PricingListBrandDropdown_option', {
                    active: isSelected(m),
                    hasDeal: hasDeal(m)
                })}
                onClick={() => selectMake(m)}
            >
                {m}
            </li>
        )
    }

    return (
        <div className={'PricingListBrandDropdown'} ref={containerRef}>
            <button
                type="button"
                className={'PricingListBrandDropdown_trigger'}
                aria-haspopup="listbox"
                aria-expanded={isOpen}
                onClick={() => isOpen ? closeDropdown() : setIsOpen(true)}
            >
                <span>{make}</span>
                <ExpandMoreIcon
                    className={classnames('PricingListBrandDropdown_caret', {
                        open: isOpen
                    })}
                />
            </button>

            {
                isOpen &&
                <div className={'PricingListBrandDropdown_menu'}>
                    <div className={'PricingListBrandDropdown_search'}>
                        <span><SearchIcon/></span>
                        <input
                            ref={searchInputRef}
                            type="text"
                            placeholder={'Search brands'}
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                        />
                    </div>
                    <ul className={'PricingListBrandDropdown_list'} role="listbox">
                        { filteredMakes().map(renderOption) }
                    </ul>
                </div>
            }
        </div>
    )
}
